// LicensesView.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { deleteLicense, fetchLicenses, sendEmailToClient, suspendLicense } from "../utils/licensesApi";
import { logAction } from "../utils/logsApi";
import Modal from "./Modal";
import Loading from "./Loading";
import "../App.css";

const LicensesView = ({ setLicenses, users = [] }) => {
    const navigate = useNavigate();
    const userRole = Number(localStorage.getItem("userRole"));
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [allLicenses, setAllLicenses] = useState([]);
    const [connectionFilter, setConnectionFilter] = useState("all"); // "all", "connected", "disconnected"

    // Authentication check
    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
    }, [navigate]);

    const [filterText, setFilterText] = useState("");
    const [modal, setModal] = useState({
        isOpen: false,
        title: "",
        message: "",
        type: "info",
        onConfirm: null,
    });

    const openModal = (title, message, type = "info", onConfirm = null) => {
        setModal({ isOpen: true, title, message, type, onConfirm });
    };

    const closeModal = () => {
        setModal({ isOpen: false, title: "", message: "", type: "info", onConfirm: null });
    };

    const handleAddUser = () => {
        navigate("/add-user");
    };

    const handleSuspendLicense = async (licenseKey) => {
        openModal(
            "Confirm Suspension",
            `Are you sure you want to suspend this license?`,
            "confirmation",
            async () => {
                try {
                    const response = await suspendLicense(licenseKey);
                    if (response.success) {
                        const action = response.data.license.isSuspended ? "suspended" : "unsuspended";
                        openModal("Success", `License ${licenseKey} has been ${action} successfully.`, "info");
                        logAction(
                            "SUSPEND_LICENSE",
                            localStorage.getItem("userEmail"),
                            `License ${licenseKey} was ${action}`,
                            licenseKey
                        );
                        const updatedLicenses = await fetchLicenses();
                        setLicenses(updatedLicenses);
                    } else {
                        openModal("Error", `Error suspending license: ${response.message}`, "info");
                    }
                } catch (error) {
                    console.error("Error suspending license:", error);
                    openModal("Error", "An error occurred while suspending the license.", "info");
                }
            }
        );
    };


    const handleDeleteLicense = async (licenseKey) => {
        openModal(
            "Confirm Deletion",
            `Are you sure you want to delete license ${licenseKey}?`,
            "confirmation",
            async () => {
                try {
                    const response = await deleteLicense(licenseKey);
                    if (response.success) {
                        openModal("Success", "License deleted successfully!", "info");
                        logAction(
                            "DELETE_LICENSE",
                            localStorage.getItem("userEmail"),
                            `Deleted license with key: ${licenseKey}`,
                            licenseKey
                        );


                        const updatedLicenses = await fetchLicenses();
                        setLicenses(updatedLicenses.filter((license) => license && license.licenseOwnerName));
                    } else {
                        openModal("Error", `Error deleting license: ${response.error}`, "info");
                    }
                } catch (error) {
                    console.error("Error handling license deletion:", error);
                    openModal("Error", "An error occurred while deleting the license.", "info");
                }
            }
        );
    };

    const handleEditLicense = (license) => {
        openModal(
            "Confirm Edit",
            `Are you sure you want to edit license ${license.licenseKey}?`,
            "confirmation",
            () => {
                localStorage.setItem("currentLicense", JSON.stringify(license));
                navigate(`/edit-license`, { state: { license } });
            }
        );
    };

    const calculateRemainingDays = (expirationDate) => {
        if (!expirationDate) return "";
        const today = new Date();
        const expiration = new Date(expirationDate);
        const diffTime = expiration - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const unitMeasure = diffDays === 1 ? "DAY" : "DAYS";
        return diffDays > 0 ? `${diffDays} ${unitMeasure}` : "Expired";
    };

    const handleLicenseClick = (license) => {
        openModal(
            "Mail Clients Details",
            `Send details of license to client?`,
            "confirmation",
            () => {
                sendEmailToClient(license);
                closeModal()
            }
        );
    };

    const copyToClipboard = (lisenceKey) => {
        navigator.clipboard.writeText(lisenceKey);
        openModal(
            `License ${lisenceKey} has been Copied!`
        );
    }

    useEffect(() => {
        const fetchAllLicenses = async () => {
            setLoading(true);
            try {
                const firstPageResponse = await fetchLicenses(1);
                let allData = [...firstPageResponse.licenses];
                let totalPagesFetched = firstPageResponse.totalPages || 1;

                if (totalPagesFetched > 1) {
                    const requests = [];
                    for (let i = 2; i <= totalPagesFetched; i++) {
                        requests.push(fetchLicenses(i));
                    }

                    const responses = await Promise.all(requests);
                    responses.forEach((res) => {
                        if (res.licenses) allData = [...allData, ...res.licenses];
                    });
                }

                setAllLicenses(allData);
                setTotalPages(Math.ceil(allData.length / 20));
            } catch (error) {
                console.error("Error fetching licenses:", error);
            }
            setLoading(false);
        };

        fetchAllLicenses();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "ArrowLeft" && currentPage > 1) {
                setCurrentPage((prev) => Math.max(prev - 1, 1));
            } else if (event.key === "ArrowRight" && currentPage < totalPages) {
                setCurrentPage((prev) => Math.min(prev + 1, totalPages));
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [currentPage, totalPages]);

    // First apply the search filter
    const searchFilteredLicenses = allLicenses.filter((license) =>
        license.licenseOwnerName &&
        (
            String(license.licenseOwnerName).toLowerCase().includes(filterText.toLowerCase()) ||
            String(license.licenseOwner || "").includes(filterText) ||
            String(license.licenseKey || "").toLowerCase().includes(filterText.toLowerCase()) ||
            String(license.assignedIp || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    // Then apply the connection filter on top of search results
    const displayedLicenses = connectionFilter === "connected"
        ? searchFilteredLicenses.filter(license => license.isOnline)
        : connectionFilter === "disconnected"
        ? searchFilteredLicenses.filter(license => !license.isOnline)
        : searchFilteredLicenses;

    const paginatedLicenses = displayedLicenses.slice((currentPage - 1) * 20, currentPage * 20);
    const totalFilteredPages = Math.ceil(displayedLicenses.length / 20);
    
    useEffect(() => {
        setCurrentPage(1);
    }, [filterText, connectionFilter]);

    return (
        <div>
            <h2 className="sub-headline">Licenses</h2>
            {userRole >= 1 && (
                <button className="add-user-button" onClick={handleAddUser}>
                    +
                </button>
            )}
            <div className="filter-container">
                <input
                    type="text"
                    placeholder="🔍 Filter by License Key / Owner Name / Owner ID / Assigned IP"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="filter-input"
                />
            </div>
            <div className="filter-container" style={{ marginBottom: 8 }}>
                <div style={{ width: '100%' }}>
                    <label htmlFor="connectionFilter" style={{ fontSize: '13px', color: '#888', marginBottom: 2, display: 'block' }}>Status:</label>
                    <select
                        id="connectionFilter"
                        value={connectionFilter}
                        onChange={(e) => setConnectionFilter(e.target.value)}
                        className="filter-input"
                    >
                        <option value="all">All</option>
                        <option value="connected">Connected</option>
                        <option value="disconnected">Disconnected</option>
                    </select>
                </div>
            </div>
            {loading ? (
                <Loading message="Loading licenses..." />
            ) : displayedLicenses.length === 0 ? (
                <p>No licenses available.</p>
            ) : (
                <table border="1" style={{ margin: "20px auto", width: "80%" }}>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>License Owner Id</th>
                            <th>License Owner Name</th>
                            <th>License Key</th>
                            <th>Client Connected</th>
                            <th>Creation Date</th>
                            <th>Expiration Date</th>
                            <th>Time Remaining</th>
                            <th>Product</th>
                            <th>Assigned IP</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedLicenses.map((license, index) => (
                            <tr key={license.licenseKey || index}>
                                <td>{(currentPage - 1) * 20 + index + 1}</td>
                                <td>{license.licenseOwner || "N/A"}</td>
                                <td>{license.licenseOwnerName || "N/A"}</td>
                                <td >
                                    <button
                                        onClick={() => handleLicenseClick(license)}
                                        onMouseEnter={(e) => {
                                            e.target.style.textDecoration = "underline";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.textDecoration = "none";
                                        }}
                                        style={{
                                            width: "100%",
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            font: "unset",
                                        }}
                                    >
                                        {license.licenseKey || "N/A"}
                                    </button>

                                </td>
                                <td>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        background: license.isOnline ? 'limegreen' : '#333',
                                        boxShadow: license.isOnline ? '0 0 8px 2px #39ff14' : 'none',
                                        margin: '0 auto'
                                    }}></span>
                                </td>
                                <td>{license.creationDate}</td>
                                <td>
                                    {license.expirationDate
                                        ? new Date(license.expirationDate).toLocaleDateString("en-GB")
                                        : ""}
                                </td>
                                <td>{calculateRemainingDays(license.expirationDate)}</td>
                                <td>{license.product || ""}</td>
                                <td>
                                    {license.assignedIp || "N/A"}
                                </td>
                                <td>

                                    <button
                                        onClick={() => copyToClipboard(license.licenseKey)}
                                        title="Copy License Key"
                                    >
                                        📋 Copy
                                    </button>
                                    {license.assignedIp && (
                                        <button
                                            style={{ marginLeft: 8, cursor: "pointer" }}
                                            onClick={() => navigator.clipboard.writeText(license.assignedIp)}
                                            title="Copy IP"
                                        >
                                            📋 Copy IP
                                        </button>
                                    )}
                                    {license.expirationDate && (
                                        <button onClick={() => handleEditLicense(license)}>
                                            Edit
                                        </button>
                                    )}
                                    <button onClick={() => handleDeleteLicense(license.licenseKey)}>Delete</button>
                                    {license.expirationDate && license.activations.length > 0 && (
                                        <button
                                            onClick={() => handleSuspendLicense(license.licenseKey)}
                                            title={license.isSuspended ? "Unsuspend License" : "Suspend License"}
                                        >
                                            {license.isSuspended ? "🔐 Unsuspend" : "🔒 Suspend"}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div className="pagination-controls">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1 || totalFilteredPages === 0}
                    title="Previous Page (←)"
                >
                    &lt;
                </button>

                <span>Page {displayedLicenses.length > 0 ? currentPage : 0} of {totalFilteredPages}</span>
                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalFilteredPages))}
                    disabled={currentPage === totalFilteredPages || totalFilteredPages === 0}
                    title="Next Page (→)"
                >
                    &gt;
                </button>

            </div>

            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                onClose={closeModal}
                onConfirm={modal.onConfirm}
            />
        </div>
    );
};

export default LicensesView;
